import React, { FC, ReactElement } from 'react'
import { format, isSameDay, parseISO } from 'date-fns'
import { includes } from 'lodash'
import { useTranslation } from 'react-i18next'
import { TableDivider } from '../../../components/Common/TableDivider'
import { authorizedPersonStatus, dividePeopleCategories } from '../../../constants'
import { ContractAuthorizedPersonType, ContractAuthPersonOrderBy } from '../../../graphql'
import { contractsTeamPageStateVar } from '../../../graphql/local'
import { usePageFiltersSorting } from '../../../hooks'

export const TeamTableDivider: FC<{
  team: ContractAuthorizedPersonType[]
  index: number
  children: ReactElement
}> = ({ team, index, children }) => {
  const { t } = useTranslation()

  const { pageFilters } = usePageFiltersSorting(contractsTeamPageStateVar)
  const { sortBy, statuses } = pageFilters

  if (!includes(dividePeopleCategories, sortBy) || team.length <= 5) {
    return <>{children}</>
  }

  const prevPerson = team[index - 1]
  const currentPerson = team[index]
  const nextPerson = team[index + 1]

  const isNotLastRow = index + 1 !== team.length

  const checkIsSameDate = (
    first: string | null | undefined,
    second: string | null | undefined,
  ): boolean => {
    if (!first || !second) return first !== second
    return !isSameDay(new Date(first), new Date(second))
  }

  const dividerFactory = {
    [ContractAuthPersonOrderBy.Status]: () => ({
      isShowDivider: statuses.length !== 1 && currentPerson?.status !== prevPerson?.status,
      isBorderBottomHidden:
        statuses.length !== 1 && currentPerson?.status !== nextPerson?.status && isNotLastRow,
      dividerText: t(authorizedPersonStatus[currentPerson?.status as string]),
    }),

    [ContractAuthPersonOrderBy.InvitedAt]: () => ({
      isShowDivider: checkIsSameDate(currentPerson?.invitationDate, prevPerson?.invitationDate),
      isBorderBottomHidden:
        checkIsSameDate(currentPerson?.invitationDate, nextPerson?.invitationDate) && isNotLastRow,
      dividerText: `${t('invitedOn', 'Invited on')} ${
        currentPerson?.invitationDate
          ? format(parseISO(currentPerson.invitationDate), 'dd.MM.yyyy')
          : t('never', 'Never')
      }`,
    }),

    [ContractAuthPersonOrderBy.LastSeen]: () => ({
      isShowDivider: checkIsSameDate(
        currentPerson?.person?.lastSeenAt,
        prevPerson?.person?.lastSeenAt,
      ),
      isBorderBottomHidden:
        checkIsSameDate(currentPerson?.person?.lastSeenAt, nextPerson?.person?.lastSeenAt) &&
        isNotLastRow,
      dividerText: `${t('lastSeenOn', 'Last seen on')} ${
        currentPerson?.person?.lastSeenAt
          ? format(parseISO(currentPerson.person?.lastSeenAt), 'dd.MM.yyyy')
          : t('never', 'Never')
      }`,
    }),
  }

  const { isShowDivider, isBorderBottomHidden, dividerText } = dividerFactory[
    sortBy as keyof typeof dividerFactory
  ]()

  return (
    <>
      {isShowDivider && <TableDivider dividerText={dividerText} />}
      {React.cloneElement(children, { hideBorderBottom: isBorderBottomHidden })}
    </>
  )
}
